import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Collapse,
} from '@mui/material';
import type {
  ApiKeyItem,
  ApiKeyListResponse,
  KeyScope,
  CreateApiKeyRequest,
  RateLimitInfo,
} from '../services/api';
import {
  getApiKeys,
  createApiKey,
  rotateApiKey,
  revokeApiKey,
  getRateLimits,
  createTokenApi,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePartnerScope } from '../contexts/PartnerScopeContext';
import { SelectPartnerPrompt } from '../components/SelectPartnerPrompt';
import { WebhookConfig } from '../components/WebhookConfig';
import { ImpersonationReadOnlyBanner } from '../components/ImpersonationReadOnlyBanner';

// ── Scope label mapping ──────────────────────────────────────────────

const SCOPE_LABELS: Record<KeyScope, string> = {
  'transcripts:read': 'Transcripts',
  'transcripts:download': 'Download',
  'course_mapping:read': 'Mappings',
  'user_management:read': 'Users',
};

const ALL_SCOPES: { value: KeyScope; label: string }[] = [
  { value: 'transcripts:read', label: 'Read Transcripts' },
  { value: 'transcripts:download', label: 'Download Transcripts' },
  { value: 'course_mapping:read', label: 'Read Course Mappings' },
  { value: 'user_management:read', label: 'Read User Data' },
];

// ── Helpers ──────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function isExpiringSoon(dateStr: string): boolean {
  const diff = new Date(dateStr).getTime() - Date.now();
  return diff > 0 && diff < 30 * 24 * 60 * 60 * 1000;
}

// ── KeyStatusChip ────────────────────────────────────────────────────

function KeyStatusChip({ status }: { status: ApiKeyItem['status'] }) {
  const config: Record<
    string,
    { color: 'success' | 'warning' | 'default' | 'error'; label: string; tooltip?: string }
  > = {
    active: { color: 'success', label: 'Active' },
    rotating: {
      color: 'warning',
      label: 'Rotating',
      tooltip: '24h grace period — old key still valid',
    },
    revoked: { color: 'default', label: 'Revoked' },
    expired: { color: 'error', label: 'Expired' },
  };
  const c = config[status] ?? config.active!;
  const chip = <Chip size="small" color={c!.color} label={c!.label} />;
  return c!.tooltip ? <Tooltip title={c!.tooltip}>{chip}</Tooltip> : chip;
}

// ── ScopeChips ───────────────────────────────────────────────────────

function ScopeChips({ scopes }: { scopes: KeyScope[] }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {scopes.map((s) => (
        <Chip key={s} label={SCOPE_LABELS[s] || s} size="small" variant="outlined" />
      ))}
    </Box>
  );
}

// ── KeyRevealDialog ──────────────────────────────────────────────────

function KeyRevealDialog({
  open,
  fullKey,
  onClose,
  onCopied,
}: {
  open: boolean;
  fullKey: string;
  onClose: () => void;
  onCopied: () => void;
}) {
  const handleCopy = async () => {
    await navigator.clipboard.writeText(fullKey);
    onCopied();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>API Key Created</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Copy this key now. It will not be shown again.
        </Alert>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            bgcolor: '#f5f5f5',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontSize: 14,
              wordBreak: 'break-all',
              flex: 1,
            }}
          >
            {fullKey}
          </Typography>
          <Tooltip title="Copy to clipboard">
            <IconButton size="small" onClick={handleCopy}>
              <i className="fa-thin fa-copy" style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── CreateKeyDialog ──────────────────────────────────────────────────

function CreateKeyDialog({
  open,
  onClose,
  onCreated,
  client,
  partnerId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (fullKey: string) => void;
  client: import('../services/api').ApiClient;
  partnerId: number;
}) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<Set<KeyScope>>(new Set());
  const [expiration, setExpiration] = useState<'90d' | '180d' | '1y' | 'custom'>('1y');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleScope = (scope: KeyScope) => {
    const next = new Set(scopes);
    if (next.has(scope)) next.delete(scope);
    else next.add(scope);
    setScopes(next);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    if (scopes.size === 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const req: CreateApiKeyRequest = {
        name: name.trim(),
        scopes: Array.from(scopes),
        expiration_policy: expiration,
      };
      const res = await createApiKey(partnerId, req, client);
      // Reset form
      setName('');
      setScopes(new Set());
      setExpiration('1y');
      onCreated(res.key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create key');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setScopes(new Set());
    setExpiration('1y');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create API Key</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          label="Name"
          placeholder="e.g. Production Integration"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          sx={{ mt: 1, mb: 2 }}
        />

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Permissions
        </Typography>
        <Box sx={{ mb: 2 }}>
          {ALL_SCOPES.map((s) => (
            <FormControlLabel
              key={s.value}
              control={
                <Checkbox
                  checked={scopes.has(s.value)}
                  onChange={() => toggleScope(s.value)}
                  size="small"
                />
              }
              label={s.label}
              sx={{ display: 'block' }}
            />
          ))}
        </Box>

        <FormControl fullWidth size="small">
          <InputLabel>Expiration</InputLabel>
          <Select
            value={expiration}
            label="Expiration"
            onChange={(e) => setExpiration(e.target.value as typeof expiration)}
          >
            <MenuItem value="90d">90 days</MenuItem>
            <MenuItem value="180d">180 days</MenuItem>
            <MenuItem value="1y">1 year</MenuItem>
            <MenuItem value="custom">Custom date</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={submitting || !name.trim() || scopes.size === 0}
        >
          {submitting ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── ConfirmDialog ────────────────────────────────────────────────────

function ConfirmDialog({
  open,
  title,
  body,
  severity,
  confirmLabel,
  confirmColor,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: React.ReactNode;
  severity?: 'error' | 'warning';
  confirmLabel: string;
  confirmColor?: 'error' | 'primary';
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    await onConfirm();
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {severity && (
          <Alert severity={severity} sx={{ mb: 2 }}>
            {body}
          </Alert>
        )}
        {!severity && <Typography>{body}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={confirmColor || 'primary'}
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={20} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Page ────────────────────────────────────────────────────────

export function ApiKeys(): React.ReactElement {
  const { idToken } = useAuth();
  const { activePartnerId: partnerId, isInternal } = usePartnerScope();
  const PARTNER_ID = partnerId ?? 0;
  const tokenClient = useMemo(() => createTokenApi(idToken), [idToken]);

  const [keys, setKeys] = useState<ApiKeyListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rateLimit, setRateLimit] = useState<RateLimitInfo | null>(null);

  // Dialog states
  const [createOpen, setCreateOpen] = useState(false);
  const [revealKey, setRevealKey] = useState<string | null>(null);
  const [rotateTarget, setRotateTarget] = useState<ApiKeyItem | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyItem | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [revokedOpen, setRevokedOpen] = useState(false);

  const fetchKeys = useCallback(async () => {
    if (partnerId == null) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getApiKeys(PARTNER_ID, tokenClient);
      setKeys(data);
    } catch (err) {
      console.error('[ApiKeys] Failed to fetch:', err);
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [tokenClient, partnerId, PARTNER_ID]);

  useEffect(() => {
    if (partnerId == null) return;
    fetchKeys();
    getRateLimits(PARTNER_ID, tokenClient)
      .then(setRateLimit)
      .catch((err) => console.error('[ApiKeys] Failed to fetch rate limits:', err));
  }, [fetchKeys, tokenClient, partnerId, PARTNER_ID]);

  // Active + rotating keys
  const activeKeys = keys ? [...keys.active] : [];
  // Revoked + expired
  const inactiveKeys = keys ? [...keys.revoked, ...keys.expired] : [];

  const handleKeyCreated = (fullKey: string) => {
    setCreateOpen(false);
    setRevealKey(fullKey);
    fetchKeys();
  };

  const handleRotateConfirm = async () => {
    if (!rotateTarget) return;
    try {
      const res = await rotateApiKey(PARTNER_ID, rotateTarget.id, tokenClient);
      setRotateTarget(null);
      setRevealKey(res.new_key.key);
      fetchKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate key');
      setRotateTarget(null);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    try {
      await revokeApiKey(PARTNER_ID, revokeTarget.id, tokenClient);
      setRevokeTarget(null);
      fetchKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
      setRevokeTarget(null);
    }
  };

  const handleCopyPrefix = async (prefix: string) => {
    await navigator.clipboard.writeText(prefix);
    setSnackbar('Copied to clipboard');
  };

  const isEmpty = !loading && keys && activeKeys.length === 0 && inactiveKeys.length === 0;

  if (partnerId == null) {
    return <SelectPartnerPrompt />;
  }

  return (
    <>
      {/* Header band — cool grey */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ pt: 4, pb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h1" gutterBottom>
                API Keys
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Manage API credentials for programmatic access
                </Typography>
                {rateLimit && (
                  <Chip
                    size="small"
                    label={`${rateLimit.requests_per_hour.toLocaleString()} requests/hr`}
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
            {!isInternal && (
              <Button variant="contained" onClick={() => setCreateOpen(true)} sx={{ mt: 0.5 }}>
                Create API Key
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      {/* Content area — white */}
      <Box sx={{ bgcolor: 'background.paper', flex: 1 }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <ImpersonationReadOnlyBanner />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={32} />
            </Box>
          ) : isEmpty ? (
            /* Empty state */
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <i
                className="fa-thin fa-key"
                style={{ fontSize: 48, color: '#98A4AE', marginBottom: 16 }}
              />
              <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                No API keys yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {isInternal
                  ? 'This partner has not created any API keys'
                  : 'Create your first API key to get started'}
              </Typography>
              {!isInternal && (
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                  Create API Key
                </Button>
              )}
            </Box>
          ) : (
            <>
              {/* Active keys table */}
              {activeKeys.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Active Keys
                  </Typography>
                  <Paper sx={{ mb: 3 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Key</TableCell>
                            <TableCell>Scopes</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Expires</TableCell>
                            {!isInternal && <TableCell align="center">Actions</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeKeys.map((k) => (
                            <TableRow key={k.id} hover>
                              <TableCell>{k.name}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                                    {k.key_prefix}
                                    {'••••'}
                                  </Typography>
                                  <Tooltip title="Copy prefix">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCopyPrefix(k.key_prefix)}
                                    >
                                      <i className="fa-thin fa-copy" style={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <ScopeChips scopes={k.scopes} />
                              </TableCell>
                              <TableCell>
                                <KeyStatusChip status={k.status} />
                              </TableCell>
                              <TableCell>{formatDate(k.created_at)}</TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isExpiringSoon(k.expires_at) ? 'error.main' : 'inherit',
                                    fontWeight: isExpiringSoon(k.expires_at) ? 600 : 400,
                                  }}
                                >
                                  {formatDate(k.expires_at)}
                                </Typography>
                              </TableCell>
                              {!isInternal && (
                                <TableCell align="center">
                                  <Tooltip title="Rotate key">
                                    <IconButton size="small" onClick={() => setRotateTarget(k)}>
                                      <i className="fa-thin fa-rotate" style={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Revoke key">
                                    <IconButton size="small" onClick={() => setRevokeTarget(k)}>
                                      <i className="fa-thin fa-ban" style={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </>
              )}

              {/* Revoked / Expired — collapsible */}
              {inactiveKeys.length > 0 && (
                <>
                  <Box
                    onClick={() => setRevokedOpen(!revokedOpen)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      mb: 1,
                      userSelect: 'none',
                    }}
                  >
                    <i
                      className={`fa-thin fa-chevron-${revokedOpen ? 'down' : 'right'}`}
                      style={{ fontSize: 14, color: '#6D6E71' }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Revoked / Expired ({inactiveKeys.length})
                    </Typography>
                  </Box>
                  <Collapse in={revokedOpen}>
                    <Paper sx={{ opacity: 0.7 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Key</TableCell>
                              <TableCell>Scopes</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Created</TableCell>
                              <TableCell>Expires</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {inactiveKeys.map((k) => (
                              <TableRow key={k.id}>
                                <TableCell>{k.name}</TableCell>
                                <TableCell>
                                  <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                                    {k.key_prefix}
                                    {'••••'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <ScopeChips scopes={k.scopes} />
                                </TableCell>
                                <TableCell>
                                  <KeyStatusChip status={k.status} />
                                </TableCell>
                                <TableCell>{formatDate(k.created_at)}</TableCell>
                                <TableCell>{formatDate(k.expires_at)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Collapse>
                </>
              )}
            </>
          )}

          <WebhookConfig />
        </Container>
      </Box>

      {/* Dialogs */}
      <CreateKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleKeyCreated}
        client={tokenClient}
        partnerId={PARTNER_ID}
      />

      <KeyRevealDialog
        open={!!revealKey}
        fullKey={revealKey || ''}
        onClose={() => setRevealKey(null)}
        onCopied={() => setSnackbar('Copied to clipboard')}
      />

      <ConfirmDialog
        open={!!rotateTarget}
        title="Rotate API Key"
        body={
          <>
            This will generate a new key for <strong>{rotateTarget?.name}</strong>. The current key
            will remain valid for 24 hours to allow you to update your integrations.
          </>
        }
        confirmLabel="Rotate"
        onClose={() => setRotateTarget(null)}
        onConfirm={handleRotateConfirm}
      />

      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke API Key"
        severity="error"
        body={
          <>
            This will immediately invalidate <strong>{revokeTarget?.name}</strong>. Any integrations
            using this key will stop working.
          </>
        }
        confirmLabel="Revoke"
        confirmColor="error"
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirm}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </>
  );
}
